/**
 * "Never wonder where" (docs/adr/0043-unified-folders.md §5): after anything is created, uploaded,
 * imported or converted, the app says "บันทึกไว้ที่: <โฟลเดอร์>" with "ย้าย…" — and when there was no
 * folder to put it in, "เลือกโฟลเดอร์…" opens the folder picker with a suggestion.
 *
 *   announceSaved({ kind: 'design', ids: [d.id], folderId: d.folderId, title: d.title })
 *
 * works from anywhere (pages, the editor, upload queues); <SavedAtHost /> in App turns it into the
 * toast. <SaveLocation /> is the line create / import pages show BEFORE saving: "จะบันทึกไว้ที่ …".
 */
import { lazy, Suspense, useEffect, useState } from 'react';
import FolderInput from 'lucide-react/dist/esm/icons/folder-input.mjs';
import { api, describeError } from '../../api/client.js';
import type { Folder, FolderScope } from '../../api/types.js';
import { folderById, folderTrail, invalidateFolders, suggestFolder, useFolderTree } from '../../app/folders.js';
import { useSession } from '../../app/session.js';
import { useToast } from '../Toast.js';

const FolderPickerDialog = lazy(() => import('./FolderDialogs.js').then((m) => ({ default: m.FolderPickerDialog })));

export interface Saved {
  kind: FolderScope;
  ids: string[];
  /** Where the server filed it (null = no folder). */
  folderId: string | null | undefined;
  /** For the suggestion when it is in no folder. */
  title?: string;
  customerId?: string | null;
  /** e.g. "งานนำเสนอ", "ไฟล์ 3 รายการ" — the subject of the sentence. */
  what?: string;
}

const listeners = new Set<(s: Saved) => void>();
/** Lists that show what was filed / moved re-read themselves. */
export const FILED_EVENT = 'xc:filed';

export function announceSaved(saved: Saved): void {
  if (!saved.ids.length) return;
  listeners.forEach((fn) => fn(saved));
}

/** Re-run `fn` when something was moved to another folder from a "บันทึกไว้ที่" notice. */
export function useFiled(fn: (kind: FolderScope) => void): void {
  useEffect(() => {
    const on = (e: Event) => fn((e as CustomEvent<{ kind: FolderScope }>).detail.kind);
    window.addEventListener(FILED_EVENT, on);
    return () => window.removeEventListener(FILED_EVENT, on);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export function SavedAtHost() {
  const [active, setActive] = useState<Saved | null>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const on = (s: Saved) => {
      setSeen(true);
      setActive(s);
    };
    listeners.add(on);
    return () => void listeners.delete(on);
  }, []);
  // The tree is asked for only once something has been saved (the editor never needs it otherwise).
  return seen ? <SavedAtNotice saved={active} /> : null;
}

function SavedAtNotice({ saved }: { saved: Saved | null }) {
  const ws = useSession().workspace;
  const notify = useToast();
  const { folders } = useFolderTree();
  const [told, setTold] = useState<Saved | null>(null);
  const [moving, setMoving] = useState<Saved | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!saved || saved === told || !folders) return;
    setTold(saved);
    invalidateFolders(ws?.id);
    const trail = saved.folderId ? folderTrail(folders, saved.folderId) : '';
    const subject = saved.what ? `${saved.what} ` : '';
    if (trail) notify(`${subject}บันทึกไว้ที่: ${trail}`, 'success', { label: 'ย้าย…', onClick: () => setMoving(saved) });
    else if (saved.folderId) notify(`${subject}บันทึกแล้ว`, 'success');
    else notify(`${subject}บันทึกแล้ว — ยังไม่ได้อยู่ในโฟลเดอร์`, 'info', { label: 'เลือกโฟลเดอร์…', onClick: () => setMoving(saved) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved, folders]);

  if (!moving || !ws) return null;
  const suggestion: Folder | null = moving.folderId ? null : suggestFolder({ title: moving.title ?? '', customerId: moving.customerId }, folders);
  const move = async (folderId: string | null) => {
    setBusy(true);
    setError(null);
    try {
      for (let i = 0; i < moving.ids.length; i += 100) await api.organize(ws.id, { kind: moving.kind, ids: moving.ids.slice(i, i + 100), folderId });
      invalidateFolders(ws.id);
      window.dispatchEvent(new CustomEvent(FILED_EVENT, { detail: { kind: moving.kind } }));
      const name = folderById(folders, folderId)?.name;
      notify(name ? `ย้ายไปที่โฟลเดอร์ “${name}” แล้ว` : 'นำออกจากโฟลเดอร์แล้ว', 'success');
      setMoving(null);
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Suspense fallback={null}>
      <FolderPickerDialog
        title={moving.folderId ? 'ย้ายไปที่โฟลเดอร์…' : 'เก็บไว้ที่โฟลเดอร์…'}
        folders={folders}
        current={moving.folderId ?? suggestion?.id ?? undefined}
        preselected={!moving.folderId && !!suggestion}
        hint={suggestion ? `แนะนำ: ${folderTrail(folders, suggestion.id)} (${moving.customerId && suggestion.customerId === moving.customerId ? 'ลูกค้าเดียวกัน' : 'ชื่อคล้ายกัน'})` : undefined}
        rootLabel={moving.folderId ? 'ไม่อยู่ในโฟลเดอร์ (ยังไม่จัดโฟลเดอร์)' : undefined}
        confirmLabel="เก็บไว้ที่นี่"
        busy={busy}
        error={error}
        onPick={(id) => void move(id)}
        onClose={() => setMoving(null)}
      />
    </Suspense>
  );
}

/** "จะบันทึกไว้ที่: ลูกค้า › กรมป่าไม้ · เปลี่ยน" — on pages that create something outside the lists. */
export function SaveLocation({ folders, folderId, onChange, verb = 'จะบันทึกไว้ที่', noneLabel }: { folders: Folder[] | null; folderId: string | null; onChange: (id: string | null) => void; verb?: string; /** What "no folder" means on this page (ADR 0052: the person's own space). */ noneLabel?: string }) {
  const [picking, setPicking] = useState(false);
  const trail = folderTrail(folders, folderId);
  return (
    <p className="xc-save-loc">
      <FolderInput size={16} aria-hidden="true" />
      <span>
        {verb}: <strong>{trail || noneLabel || 'ยังไม่ได้เลือกโฟลเดอร์'}</strong>
      </span>
      <button type="button" className="xc-link-btn" onClick={() => setPicking(true)}>
        {trail ? 'เปลี่ยน…' : noneLabel ? 'เลือกโฟลเดอร์ทีม…' : 'เลือกโฟลเดอร์…'}
      </button>
      {picking && (
        <Suspense fallback={null}>
          <FolderPickerDialog
            title="เลือกโฟลเดอร์ที่จะบันทึก"
            folders={folders}
            current={folderId}
            rootLabel={noneLabel ?? 'ยังไม่จัดโฟลเดอร์'}
            confirmLabel="ใช้โฟลเดอร์นี้"
            onPick={(id) => {
              onChange(id);
              setPicking(false);
            }}
            onClose={() => setPicking(false)}
          />
        </Suspense>
      )}
    </p>
  );
}
